import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { PurchaseOrderDetails } from "../../../../contract/dtos/purchaseRequest";

// components
import BreadCrumbs from "../../components/breadCrumb";
import Badge from "../../components/badge";
import Pagination from "../../containers/table/pagination";

// Local components
import { LoadingSkeleton } from "./components/SkeletonComponents";
import { ProductTable } from "./components/ProductTable";
import { OrderSummary } from "./components/OrderSummary";
import { ErrorState } from "./components/ErrorState";

// Types
import { PurchaseOrderDetailsViewProps } from "./types";

function PurchaseOrderDetailsView({
  tenantId,
  purchaseOrderId,
  componentId,
  breadcrumbItems,
  userPermissions = [],
  canViewPurchaseRequests = true,
  isSuperAdmin = false,
  PurchaseRequestDetailsModal,
  showSkeletonLoading = false,
  VIEW_PURCHASE_REQUEST_PERMISSION = "VIEW_PURCHASE_REQUEST",
}: PurchaseOrderDetailsViewProps) {
  const [selectedPRForDetails, setSelectedPRForDetails] = useState<{
    prId: string;
    branchId: string;
  } | null>(null);

  // Add modal handler
  const handlePRClick = (prId: string, branchId: string) => {
    setSelectedPRForDetails({ prId, branchId });
  };

  // Fetch purchase order details
  const {
    data: purchaseOrderResponse,
    isLoading,
    error,
  } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getPurchaseOrderById({
      componentId: componentId || "",
      tenantId: tenantId,
      poId: purchaseOrderId || "",
    }),
    enabled: !!(purchaseOrderId && tenantId && componentId),
    select: (data: any) => {
      return {
        data: data?.data,
        headers: { "x-total-count": "1" },
      };
    },
  });

  // Extract data and headers
  const purchaseOrder = purchaseOrderResponse?.data;

  // Update pagination state to match creation view
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 4; // Match the creation view

  // Calculate total pages from the actual data length - match creation view
  const totalPagesProducts = Math.ceil(
    (purchaseOrder?.items?.length || 0) / ITEMS_PER_PAGE
  );

  // Add pagination logic for the products - match creation view approach
  const getPaginatedProducts = () => {
    if (!purchaseOrder) return [];

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return purchaseOrder.items?.slice(startIndex, endIndex) || [];
  };

  // Pagination handlers - match creation view
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPagesProducts) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Show skeleton loading state
  if (isLoading && showSkeletonLoading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState />;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumbs */}
          <BreadCrumbs items={breadcrumbItems} />

          {/* Page Title and Actions */}
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                View purchase order
              </h1>
              <p className="text-sm text-gray-600 mt-1">{purchaseOrderId}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Products */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Products Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">Products</h2>
                    <div className="flex gap-2">
                      <Badge variant="default" className="!rounded-sm bg-white">
                        {getPaginatedProducts().length || 0} total
                      </Badge>
                      <Badge variant="default" className="!rounded-sm bg-white">
                        {(purchaseOrder as PurchaseOrderDetails)
                          ?.number_of_products || 0}{" "}
                        items
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <ProductTable
                products={{
                  ...(purchaseOrder as PurchaseOrderDetails),
                  items: getPaginatedProducts(),
                }}
                orderSummary={
                  purchaseOrder?.order_summary as PurchaseOrderDetails["order_summary"]
                }
              />

              {/* Pagination */}
              <Pagination
                currentPage={currentPage - 1}
                totalPages={totalPagesProducts}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                onPageChange={(page) => handlePageChange(page + 1)}
              />
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <OrderSummary
              purchaseOrder={purchaseOrder as PurchaseOrderDetails}
              userPermissions={userPermissions}
              canViewPurchaseRequests={canViewPurchaseRequests}
              isSuperAdmin={isSuperAdmin}
              VIEW_PURCHASE_REQUEST_PERMISSION={
                VIEW_PURCHASE_REQUEST_PERMISSION
              }
              onPRClick={handlePRClick}
            />
          </div>
        </div>
      </div>

      {PurchaseRequestDetailsModal && (
        <PurchaseRequestDetailsModal
          open={
            !!selectedPRForDetails &&
            !!selectedPRForDetails?.branchId &&
            !!selectedPRForDetails?.prId
          }
          onClose={() => setSelectedPRForDetails(null)}
          purchaseRequestId={selectedPRForDetails?.prId || ""}
          branchId={selectedPRForDetails?.branchId || ""}
          componentId={componentId || ""}
          tenantId={tenantId || ""}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </div>
  );
}

export default PurchaseOrderDetailsView;
export type { PurchaseOrderDetailsViewProps, BreadcrumbItem } from "./types";
